import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getAllBusOwnersReport, getBusOwnerReport } from '../../Services/Operations/ProductAPI';
import '../../Styles/ManagementPages.css';
import '../../Styles/BusOwnerReport.css';
import { toast } from 'react-toastify';

const SkeletonStatsCard = () => (
  <div className="stat-card skeleton">
    <div className="skeleton-title"></div>
    <div className="skeleton-value"></div>
    <div className="skeleton-subtitle"></div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="skeleton-row">
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
  </tr>
);

const SkeletonTable = () => (
  <div className="table-container skeleton">
    <table className="data-table">
      <thead>
        <tr>
          <th>No.</th>
          <th>Owner Name</th>
          <th>Contact</th>
          <th>Address</th>
          <th>Buses</th>
          <th>Seat Booking</th>
          <th>Instant Booking</th>
          <th>Razorpay</th>
          <th>TDS</th>
          <th>HDFC</th>
          <th>Penalty</th>
          <th>Refund</th>
          <th>Payable</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
          <SkeletonTableRow key={i} />
        ))}
      </tbody>
    </table>
  </div>
);

const BusOwnerReport = () => {
  const { token } = useSelector(state => state.user);
  
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewingReport, setViewingReport] = useState(null);
  const [error, setError] = useState(null);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);

  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth() - 2, 1)
      .toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalReports, setTotalReports] = useState(0);
  const [pageSize] = useState(10);

  const [stats, setStats] = useState({
    totalOwners: 0,
    totalBuses: 0,
    totalSeatBooking: 0,
    totalInstantBooking: 0,
    totalPayable: 0,
    totalPenalty: 0,
    totalRefund: 0
  });

  const getSequentialNumber = (index) => {
    return (currentPage - 1) * pageSize + index + 1;
  };

const fetchReports = async () => {
  try {
    setLoading(true);
    setError(null);

    const response = await getAllBusOwnersReport(
      token,
      dateRange.startDate,
      dateRange.endDate
    );

    console.log('Bus Owner Report Response:', response);

    if (response && response.success) {
      const transformedReports = response.data.map((report, index) => ({
        id: index,
        serialNo: getSequentialNumber(index),
        ownerName: report['Bus Owners Name'] || '',
        contractNumber: report['Contract Number'] || '',
        address: report['Address'] || '',
        noOfBuses: report['No. Of Buses'] || 0,
        seatBooking: report['Seat Booking'] || 0,
        instantBooking: report['Intant Booking'] || 0,
        razorpay: report['Razorpay(Deduction)'] || 0,
        tds: report['TDS(Deduction)'] || 0,
        hdfc: report['HDFC Deduction'] || 0,
        penalty: report['Penaulty to Customer'] || 0,
        cgst: report['CGST'] || 0,
        sgst: report['SGST'] || 0,
        igst: report['IGST'] || 0,
        refund: report['Refund to Customer'] || 0,
        payableAmount: report['Payable Amount'] || 0,
        perBus: report._perBus || []
      }));

      setReports(transformedReports);
      setTotalReports(response.count || 0);
      setTotalPages(Math.ceil((response.count || 0) / pageSize));

      calculateStats(transformedReports);
    } else {
      setReports([]);
      setTotalReports(0);
      setTotalPages(1);
      setStats({
        totalOwners: 0,
        totalBuses: 0,
        totalSeatBooking: 0,
        totalInstantBooking: 0,
        totalPayable: 0,
        totalPenalty: 0,
        totalRefund: 0
      });
    }
  } catch (err) {
    console.error('Error fetching bus owners report:', err);
    setError(err.response?.data?.message || 'Failed to fetch bus owners report. Please try again.');
    toast.error('Failed to fetch bus owners report');
  } finally {
    setLoading(false);
  }
};

  const calculateStats = (data) => {
    const totalSeatBooking = data.reduce((sum, item) => sum + item.seatBooking, 0);
    const totalInstantBooking = data.reduce((sum, item) => sum + item.instantBooking, 0);
    const totalPayable = data.reduce((sum, item) => sum + item.payableAmount, 0);
    const totalPenalty = data.reduce((sum, item) => sum + item.penalty, 0);
    const totalRefund = data.reduce((sum, item) => sum + item.refund, 0);
    const totalBuses = data.reduce((sum, item) => sum + item.noOfBuses, 0);

    setStats({
      totalOwners: data.length,
      totalBuses,
      totalSeatBooking,
      totalInstantBooking,
      totalPayable,
      totalPenalty,
      totalRefund
    });
  };

  const handleViewReport = async (ownerName) => {
    try {
      setIsFetchingDetails(true);
      setError(null);

      const owner = reports.find(r => r.ownerName === ownerName);
      if (!owner) {
        toast.warning('Owner not found in the current report');
        setViewingReport(null);
        return;
      }

      setViewingReport(owner);
    } catch (err) {
      console.error('Error fetching bus owner report details:', err);
      setError('Failed to fetch bus owner report details.');
      toast.error('Failed to fetch bus owner report details');
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const closeViewModal = () => {
    setViewingReport(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRefresh = () => {
    fetchReports();
  };

  const handleExportCSV = () => {
    if (filteredReports.length === 0) {
      toast.warning('No data to export');
      return;
    }

    const headers = [
      'Serial No.',
      'Owner Name',
      'Contact',
      'Address',
      'No. Of Buses',
      'Seat Booking',
      'Instant Booking',
      'Razorpay',
      'TDS',
      'HDFC',
      'Penalty',
      'CGST',
      'SGST',
      'IGST',
      'Refund',
      'Payable Amount'
    ];

    const csvData = filteredReports.map(report => [
      report.serialNo,
      `"${report.ownerName || ''}"`,
      `"${report.contractNumber || ''}"`,
      `"${report.address || ''}"`,
      report.noOfBuses,
      report.seatBooking.toFixed(2),
      report.instantBooking.toFixed(2),
      report.razorpay.toFixed(2),
      report.tds.toFixed(2),
      report.hdfc.toFixed(2),
      report.penalty.toFixed(2),
      report.cgst.toFixed(2),
      report.sgst.toFixed(2),
      report.igst.toFixed(2),
      report.refund.toFixed(2),
      report.payableAmount.toFixed(2)
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bus-owner-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success('CSV exported successfully!');
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const filteredReports = reports.filter(report =>
    (report.ownerName && report.ownerName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (report.contractNumber && report.contractNumber.includes(searchTerm)) ||
    (report.address && report.address.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  useEffect(() => {
    fetchReports();
  }, [dateRange.startDate, dateRange.endDate]);

  if (loading && reports.length === 0) {
    return (
      <div className="management-page">
        <div className="page-header">
          <div className="skeleton-header-title"></div>
          <div className="header-actions">
            <div className="skeleton-search"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
          </div>
        </div>

        <div className="stats-cards">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <SkeletonStatsCard key={i} />
          ))}
        </div>

        <SkeletonTable />

        <div className="pagination-container">
          <div className="skeleton-pagination-info"></div>
          <div className="skeleton-pagination-controls"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="management-page bus-owner-report-page">
      <div className="page-header">
        <h1>Bus Owner Report</h1>
        <div className="header-actions">
          <div className="filters-group">
            <div className="date-filters">
              <label>
                Start Date:
                <input
                  type="date"
                  name="startDate"
                  value={dateRange.startDate}
                  onChange={handleDateChange}
                  className="date-input"
                />
              </label>
              <label>
                End Date:
                <input
                  type="date"
                  name="endDate"
                  value={dateRange.endDate}
                  onChange={handleDateChange}
                  className="date-input"
                />
              </label>
            </div>
            <input
              type="text"
              placeholder="Search by owner name, contact or address..."
              className="search-input"
              value={searchTerm}
              onChange={handleSearchChange}
              style={{ width: "250px" }}
            />
          </div>

          <button className="refresh-btn" onClick={handleRefresh}>
            🔄 Refresh
          </button>
          <button className="export-btn" onClick={handleExportCSV}>
            📊 Export CSV
          </button>
        </div>
      </div>

      <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px', textAlign: 'center' }}>
        Total Reports: {totalReports} | Page {currentPage} of {totalPages} | 
        Date Range: {dateRange.startDate} to {dateRange.endDate}
      </div>

      {error && (
        <div className="error-message" style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '10px',
          borderRadius: '4px',
          marginBottom: '15px'
        }}>
          {error}
        </div>
      )}


      <div className="stats-cards">
        <div className="stat-card">
          <h3>Total Owners</h3>
          <p>{stats.totalOwners}</p>
          <span>Bus owners in report</span>
        </div>
        <div className="stat-card">
          <h3>Total Buses</h3>
          <p>{stats.totalBuses}</p>
          <span>Total buses across all owners</span>
        </div>
        <div className="stat-card">
          <h3>Total Seat Booking</h3>
          <p>₹{stats.totalSeatBooking.toFixed(2)}</p>
          <span>Total seat bookings</span>
        </div>
        <div className="stat-card">
          <h3>Total Instant Booking</h3>
          <p>₹{stats.totalInstantBooking.toFixed(2)}</p>
          <span>Total instant bookings</span>
        </div>
        <div className="stat-card">
          <h3>Total Payable</h3>
          <p>₹{stats.totalPayable.toFixed(2)}</p>
          <span>Total payable amount</span>
        </div>
        <div className="stat-card">
          <h3>Total Penalty</h3>
          <p>₹{stats.totalPenalty.toFixed(2)}</p>
          <span>Total penalty amount</span>
        </div>
        <div className="stat-card">
          <h3>Total Refund</h3>
          <p>₹{stats.totalRefund.toFixed(2)}</p>
          <span>Total refund amount</span>
        </div>
      </div>


      <div className="table-container" style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Owner Name</th>
              <th>Contact</th>
              <th>Address</th>
              <th>Buses</th>
              <th>Seat Booking</th>
              <th>Instant Booking</th>
              <th>Razorpay</th>
              <th>TDS</th>
              <th>HDFC</th>
              <th>Penalty</th>
              <th>Refund</th>
              <th>Payable</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredReports.length > 0 ? (
              filteredReports.map(report => (
                <tr key={report.id}>
                  <td>{report.serialNo}</td>
                  <td>
                    <strong>{report.ownerName}</strong>
                    {report.noOfBuses > 0 && (
                      <div className="bus-count">
                        <small>{report.noOfBuses} bus{report.noOfBuses > 1 ? 'es' : ''}</small>
                      </div>
                    )}
                  </td>
                  <td>{report.contractNumber}</td>
                  <td>
                    <div className="address-info" title={report.address}>
                      {report.address.length > 30 ? 
                        `${report.address.substring(0, 30)}...` : 
                        report.address}
                    </div>
                  </td>
                  <td>
                    <span className="bus-count-badge">
                      {report.noOfBuses}
                    </span>
                  </td>
                  <td>₹{report.seatBooking.toFixed(2)}</td>
                  <td>₹{report.instantBooking.toFixed(2)}</td>
                  <td>₹{report.razorpay.toFixed(2)}</td>
                  <td>₹{report.tds.toFixed(2)}</td>
                  <td>₹{report.hdfc.toFixed(2)}</td>
                  <td>
                    <span className={`penalty-amount ${report.penalty > 0 ? 'has-penalty' : ''}`}>
                      ₹{report.penalty.toFixed(2)}
                    </span>
                  </td>
                  <td>
                    <span className={`refund-amount ${report.refund > 0 ? 'has-refund' : ''}`}>
                      ₹{report.refund.toFixed(2)}
                    </span>
                  </td>
                  <td>
                    <span className="payable-amount">
                      ₹{report.payableAmount.toFixed(2)}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn view"
                        onClick={() => handleViewReport(report.ownerName)}
                      >
                        View Details
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="14" className="no-data" style={{ textAlign: 'center', padding: '40px' }}>
                  <p>
                    {searchTerm 
                      ? 'No bus owners found matching your search.' 
                      : 'No bus owner reports available for the selected date range.'}
                  </p>
                  {searchTerm && (
                    <button 
                      className="clear-filters-link" 
                      onClick={() => setSearchTerm('')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#007bff',
                        cursor: 'pointer',
                        textDecoration: 'underline'
                      }}
                    >
                      Clear search to see all owners
                    </button>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


      {totalPages > 1 && (
        <div className="pagination-container" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '20px',
          padding: '10px 0'
        }}>
          <div className="pagination-info" style={{ fontSize: '14px', color: '#666' }}>
            Showing {filteredReports.length} of {totalReports} bus owners
            (Page {currentPage} of {totalPages})
            {searchTerm && <span className="filtered-indicator" style={{ marginLeft: '10px', color: '#888' }}> • Search: "{searchTerm}"</span>}
          </div>
          <div className="pagination-controls" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              className="pagination-btn"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              style={{
                padding: '6px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: currentPage === 1 ? '#f5f5f5' : 'white',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                color: currentPage === 1 ? '#999' : '#333'
              }}
            >
              Previous
            </button>

            <div className="page-numbers" style={{ display: 'flex', gap: '4px' }}>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNumber}
                    className={`page-number ${pageNumber === currentPage ? 'active' : ''}`}
                    onClick={() => handlePageClick(pageNumber)}
                    style={{
                      padding: '6px 12px',
                      border: pageNumber === currentPage ? '2px solid #007bff' : '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: pageNumber === currentPage ? '#007bff' : 'white',
                      color: pageNumber === currentPage ? 'white' : '#333',
                      cursor: 'pointer',
                      fontWeight: pageNumber === currentPage ? 'bold' : 'normal'
                    }}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>

            <button
              className="pagination-btn"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              style={{
                padding: '6px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: currentPage === totalPages ? '#f5f5f5' : 'white',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                color: currentPage === totalPages ? '#999' : '#333'
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}


      {viewingReport && !isFetchingDetails && (
        <div className="modal-overlay" onClick={closeViewModal} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content1 bus-owner-report-modal" onClick={(e) => e.stopPropagation()} style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            padding: '20px'
          }}>
            <div className="modal-header" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #eee',
              paddingBottom: '15px',
              marginBottom: '20px'
            }}>
              <h3 style={{ margin: 0 }}>Bus Owner Report Details</h3>
              <button className="modal-close-btn" onClick={closeViewModal} style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666'
              }}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="report-details">
                <div className="report-header" style={{ 
                  textAlign: 'center', 
                  paddingBottom: '15px', 
                  borderBottom: '2px solid #f0f0f0', 
                  marginBottom: '20px' 
                }}>
                  <h2 style={{ margin: '0 0 5px 0' }}>{viewingReport.ownerName}</h2>
                  <p style={{ color: '#666', margin: '5px 0' }}>📞 {viewingReport.contractNumber}</p>
                  <p style={{ color: '#888', margin: '5px 0', fontSize: '14px' }}>{viewingReport.address}</p>
                  <span className="bus-count-badge" style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    backgroundColor: '#007bff',
                    color: 'white'
                  }}>
                    🚌 {viewingReport.noOfBuses} Bus{viewingReport.noOfBuses > 1 ? 'es' : ''}
                  </span>
                </div>

                <div className="report-section" style={{ marginBottom: '20px' }}>
                  <h4 style={{ fontSize: '15px', color: '#555', marginBottom: '12px', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
                    Financial Summary
                  </h4>
                  <div className="detail-grid" style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: '12px' 
                  }}>
                    <div className="detail-item" style={{ background: '#f8f9fa', padding: '10px 12px', borderRadius: '6px' }}>
                      <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '3px' }}>Total Seat Booking</label>
                      <span className="amount" style={{ fontSize: '16px', fontWeight: '600', display: 'block' }}>₹{viewingReport.seatBooking.toFixed(2)}</span>
                    </div>
                    <div className="detail-item" style={{ background: '#f8f9fa', padding: '10px 12px', borderRadius: '6px' }}>
                      <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '3px' }}>Total Instant Booking</label>
                      <span className="amount" style={{ fontSize: '16px', fontWeight: '600', display: 'block' }}>₹{viewingReport.instantBooking.toFixed(2)}</span>
                    </div>
                    <div className="detail-item" style={{ background: '#f8f9fa', padding: '10px 12px', borderRadius: '6px' }}>
                      <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '3px' }}>Total Gross</label>
                      <span className="amount" style={{ fontSize: '16px', fontWeight: '600', display: 'block' }}>
                        ₹{(viewingReport.seatBooking + viewingReport.instantBooking).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="report-section" style={{ marginBottom: '20px' }}>
                  <h4 style={{ fontSize: '15px', color: '#555', marginBottom: '12px', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
                    Deductions
                  </h4>
                  <div className="detail-grid" style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: '12px' 
                  }}>
                    <div className="detail-item" style={{ background: '#f8f9fa', padding: '10px 12px', borderRadius: '6px' }}>
                      <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '3px' }}>Razorpay</label>
                      <span className="amount deduction" style={{ fontSize: '16px', fontWeight: '600', display: 'block', color: '#dc3545' }}>₹{viewingReport.razorpay.toFixed(2)}</span>
                    </div>
                    <div className="detail-item" style={{ background: '#f8f9fa', padding: '10px 12px', borderRadius: '6px' }}>
                      <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '3px' }}>TDS</label>
                      <span className="amount deduction" style={{ fontSize: '16px', fontWeight: '600', display: 'block', color: '#dc3545' }}>₹{viewingReport.tds.toFixed(2)}</span>
                    </div>
                    <div className="detail-item" style={{ background: '#f8f9fa', padding: '10px 12px', borderRadius: '6px' }}>
                      <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '3px' }}>HDFC</label>
                      <span className="amount deduction" style={{ fontSize: '16px', fontWeight: '600', display: 'block', color: '#dc3545' }}>₹{viewingReport.hdfc.toFixed(2)}</span>
                    </div>
                    <div className="detail-item" style={{ background: '#f8f9fa', padding: '10px 12px', borderRadius: '6px' }}>
                      <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '3px' }}>Penalty</label>
                      <span className={`amount ${viewingReport.penalty > 0 ? 'deduction' : ''}`} style={{ 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        display: 'block',
                        color: viewingReport.penalty > 0 ? '#dc3545' : '#333'
                      }}>
                        ₹{viewingReport.penalty.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {viewingReport.cgst > 0 || viewingReport.sgst > 0 || viewingReport.igst > 0 && (
                  <div className="report-section" style={{ marginBottom: '20px' }}>
                    <h4 style={{ fontSize: '15px', color: '#555', marginBottom: '12px', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
                      Tax Summary
                    </h4>
                    <div className="detail-grid" style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                      gap: '12px' 
                    }}>
                      {viewingReport.cgst > 0 && (
                        <div className="detail-item" style={{ background: '#f8f9fa', padding: '10px 12px', borderRadius: '6px' }}>
                          <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '3px' }}>CGST</label>
                          <span className="amount tax" style={{ fontSize: '16px', fontWeight: '600', display: 'block', color: '#17a2b8' }}>₹{viewingReport.cgst.toFixed(2)}</span>
                        </div>
                      )}
                      {viewingReport.sgst > 0 && (
                        <div className="detail-item" style={{ background: '#f8f9fa', padding: '10px 12px', borderRadius: '6px' }}>
                          <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '3px' }}>SGST</label>
                          <span className="amount tax" style={{ fontSize: '16px', fontWeight: '600', display: 'block', color: '#17a2b8' }}>₹{viewingReport.sgst.toFixed(2)}</span>
                        </div>
                      )}
                      {viewingReport.igst > 0 && (
                        <div className="detail-item" style={{ background: '#f8f9fa', padding: '10px 12px', borderRadius: '6px' }}>
                          <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '3px' }}>IGST</label>
                          <span className="amount tax" style={{ fontSize: '16px', fontWeight: '600', display: 'block', color: '#17a2b8' }}>₹{viewingReport.igst.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="report-section" style={{ marginBottom: '20px' }}>
                  <h4 style={{ fontSize: '15px', color: '#555', marginBottom: '12px', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
                    Refund Information
                  </h4>
                  <div className="detail-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <label style={{ fontWeight: '500', color: '#555' }}>Refund to Customer:</label>
                    <span className={`amount ${viewingReport.refund > 0 ? 'refund' : ''}`} style={{ 
                      fontWeight: '600',
                      color: viewingReport.refund > 0 ? '#28a745' : '#333'
                    }}>
                      ₹{viewingReport.refund.toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className="report-section total-section" style={{ 
                  background: '#f8f9fa', 
                  padding: '15px', 
                  borderRadius: '8px', 
                  borderLeft: '4px solid #28a745',
                  marginBottom: '20px'
                }}>
                  <div className="detail-row total" style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    padding: '10px 0',
                    borderBottom: 'none'
                  }}>
                    <label style={{ fontSize: '18px', fontWeight: '600', color: '#333' }}>Total Payable Amount:</label>
                    <span className="total-amount" style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
                      ₹{viewingReport.payableAmount.toFixed(2)}
                    </span>
                  </div>
                </div>


                {viewingReport.perBus && viewingReport.perBus.length > 0 && (
                  <div className="report-section" style={{ marginBottom: '20px' }}>
                    <h4 style={{ fontSize: '15px', color: '#555', marginBottom: '12px', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
                      Per Bus Breakdown ({viewingReport.perBus.length} buses)
                    </h4>
                    <div className="per-bus-list" style={{ 
                      maxHeight: '200px', 
                      overflowY: 'auto',
                      border: '1px solid #eee',
                      borderRadius: '4px'
                    }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                          <tr>
                            <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Bus</th>
                            <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Registration</th>
                            <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Type</th>
                            <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Payable</th>
                          </tr>
                        </thead>
                        <tbody>
                          {viewingReport.perBus.map((bus, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <td style={{ padding: '8px' }}>{bus.busDetails}</td>
                              <td style={{ padding: '8px' }}>{bus.busRegistration}</td>
                              <td style={{ padding: '8px' }}>
                                <span className={`bus-type-badge ${bus.busType === 'AC' ? 'ac' : 'non-ac'}`} style={{
                                  display: 'inline-block',
                                  padding: '2px 8px',
                                  borderRadius: '10px',
                                  fontSize: '10px',
                                  fontWeight: '600',
                                  backgroundColor: bus.busType === 'AC' ? '#d4edda' : '#f8d7da',
                                  color: bus.busType === 'AC' ? '#155724' : '#721c24'
                                }}>
                                  {bus.busType}
                                </span>
                              </td>
                              <td style={{ padding: '8px', textAlign: 'right', fontWeight: '600', color: '#28a745' }}>
                                ₹{bus.payableAmount.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer" style={{
              display: 'flex',
              justifyContent: 'flex-end',
              paddingTop: '15px',
              borderTop: '1px solid #eee',
              marginTop: '10px'
            }}>
              <button className="modal-close-btn secondary" onClick={closeViewModal} style={{
                padding: '8px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {viewingReport && isFetchingDetails && (
        <div className="modal-overlay">
          <div className="modal-content1 skeleton-modal">
            <div className="modal-header">
              <div className="skeleton-title"></div>
              <div className="skeleton-close"></div>
            </div>
            <div className="modal-body">
              <div className="skeleton-profile-image"></div>
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <div key={i} className="skeleton-detail-row">
                  <div className="skeleton-label"></div>
                  <div className="skeleton-value"></div>
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <div className="skeleton-button"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusOwnerReport;